/* ---------------------------------------------------------------------------
   Ready Cart BD — the category chip row, on a phone.

   Two jobs, both about the same thing: a horizontally scrolling row whose
   scrollbar is hidden tells the visitor nothing about itself.

   1. FADE THE EDGE THAT HAS MORE BEHIND IT. On a 390px screen the saree row
      fits about two and a half of six chips. Where the cut happened to land on
      a chip boundary the row looked finished, so categories holding live stock
      were one swipe away with nothing on the page suggesting the swipe. This
      is the same argument the product hero makes by showing 85% of a slide:
      you do not tell someone there is more, you show them the edge of it.

   2. SCROLL THE CURRENT CHIP INTO VIEW. The row is ordered by the department's
      own sort, so on a leaf category page the maroon "you are here" chip can
      sit off-screen — the visitor lands on সুতি শাড়ি and the row appears to be
      showing some other category's siblings. Centring it also puts the chips
      either side of it within reach, which is what the row is for.

   ---------------------------------------------------------------------------
   ⚠ PROGRESSIVE, AND IT HAS TO STAY THAT WAY. Every chip is a real <a> inside
   a real scroller. With this file blocked, slow or broken the row still
   renders, still scrolls and still navigates — it just has no fade and does
   not self-centre. Nothing here may become the only way to reach a category.

   ⚠ It binds by `[data-rcb-chips]`, which `rcb-chips.liquid` puts only on rows
   it builds. The department cross-link row on the category page shares the
   `.rcb-chips` class and deliberately does not carry the attribute: it holds
   two chips, never overflows, and a fade on it would be a lie.

   Loaded deferred from `rcb-head.liquid`, so it runs after parse and costs the
   first paint nothing.
   --------------------------------------------------------------------------- */

(() => {
  const rows = document.querySelectorAll('[data-rcb-chips]');
  if (!rows.length) return;

  /* Sub-pixel scroll positions are normal — a phone's scroll is fractional and
     `scrollWidth` is rounded — so an exact comparison reports "more to the
     right" forever and the fade never clears at the end of the row. */
  const SLACK = 2;

  const paint = (row) => {
    const overflow = row.scrollWidth - row.clientWidth;
    if (overflow <= SLACK) {
      row.removeAttribute('data-edge');
      return;
    }
    const atStart = row.scrollLeft <= SLACK;
    const atEnd = row.scrollLeft >= overflow - SLACK;
    row.dataset.edge = atStart ? 'end' : atEnd ? 'start' : 'both';
  };

  const centreCurrent = (row) => {
    const overflow = row.scrollWidth - row.clientWidth;
    if (overflow <= SLACK) return;

    const current = row.querySelector('.rcb-chip--on');
    if (!current) return;

    /* `offsetLeft` is measured against the nearest positioned ancestor, which
       is not necessarily this row, so the chip's position inside the scroller
       is taken from the two rects. Assigning `scrollLeft` scrolls only this
       element — `scrollIntoView` would take the page with it and drop the
       visitor below the header on every category page. */
    const rowBox = row.getBoundingClientRect();
    const chipBox = current.getBoundingClientRect();
    const offset = chipBox.left - rowBox.left + row.scrollLeft;
    row.scrollLeft = offset - (row.clientWidth - chipBox.width) / 2;
  };

  /* Mouse drag-to-slide & wheel scroll for desktop/laptop users when horizontal
     overflow occurs (e.g. tablet or narrow windows). Allows mouse users to
     drag horizontally like a touch screen, and wheel-scroll horizontally. */
  const enableDragAndWheel = (row) => {
    let startX = 0;
    let scrollStart = 0;
    let hasDragged = false;

    const onMouseMove = (e) => {
      const dx = e.pageX - startX;
      if (Math.abs(dx) > 4) {
        hasDragged = true;
        row.classList.add('is-dragging');
        row.scrollLeft = scrollStart - dx;
      }
    };

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setTimeout(() => {
        row.classList.remove('is-dragging');
        hasDragged = false;
      }, 50);
    };

    row.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      const overflow = row.scrollWidth - row.clientWidth;
      if (overflow <= SLACK) return;

      hasDragged = false;
      startX = e.pageX;
      scrollStart = row.scrollLeft;

      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    });

    row.addEventListener(
      'click',
      (e) => {
        if (hasDragged) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );

    row.addEventListener(
      'wheel',
      (e) => {
        const overflow = row.scrollWidth - row.clientWidth;
        if (overflow <= SLACK) return;
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
          const atStart = row.scrollLeft <= SLACK && e.deltaY < 0;
          const atEnd = row.scrollLeft >= overflow - SLACK && e.deltaY > 0;
          if (!atStart && !atEnd) {
            e.preventDefault();
            row.scrollLeft += e.deltaY;
          }
        }
      },
      { passive: false }
    );
  };

  for (const row of rows) {
    centreCurrent(row);
    paint(row);
    enableDragAndWheel(row);
    row.addEventListener('scroll', () => paint(row), { passive: true });
  }

  /* Rotating a phone changes what fits. ResizeObserver rather than a window
     resize listener, because the row also changes width when the drawer opens
     and when a font swaps in — Noto Sans Bengali arrives after first paint on
     any device that does not already have it, and it remeasures every chip. */
  if ('ResizeObserver' in window) {
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) paint(entry.target);
    });
    for (const row of rows) ro.observe(row);
  }
})();
